import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Brain, List, X, ChartLine, Users, CreditCard, ArrowRight, Newspaper, ShoppingCart, Package, Info, GridFour, Bell, Heart, ChatCircle, ChartBar } from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import UserAvatar from './UserAvatar';
import { API_URL } from '../config';

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API_URL}/api/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.count);
    } catch (e) { console.error('Failed to fetch unread count:', e); }
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleOpenNotifications = async () => {
    setNotifOpen(!notifOpen);
    if (!notifOpen) {
      try {
        const res = await axios.get(`${API_URL}/api/notifications?limit=15`, { withCredentials: true });
        setNotifications(res.data);
        if (unreadCount > 0) {
          await axios.post(`${API_URL}/api/notifications/mark-read`, {}, { withCredentials: true });
          setUnreadCount(0);
        }
      } catch (e) { console.error('Failed to fetch notifications:', e); }
    }
  };

  const navLinks = [
    { href: '/dashboard', label: 'Dashboard', icon: ChartLine },
    { href: '/ai-coach', label: 'AI Coach', icon: Brain },
    { href: '/store', label: 'Store', icon: ShoppingCart },
    { href: '/crypto-news', label: 'News', icon: Newspaper },
    { href: '/tradingview', label: 'TradingView Charts', icon: ChartLine },
    { href: '/rainbow-chart', label: 'Rainbow Chart', icon: ChartLine },
    { href: '/fear-greed', label: 'Fear & Greed', icon: ChartLine },
    { href: '/community', label: 'Community', icon: Users },
    { href: '/gallery', label: 'Gallery', icon: GridFour },
    ...(user?.role === 'admin' ? [{ href: '/admin/analytics', label: 'Analytics', icon: ChartBar }] : []),
  ];

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="nav-logo">
            <Brain size={28} weight="duotone" className="text-[#007AFF]" />
            <span className="font-heading font-black text-lg tracking-tight text-white uppercase hidden sm:inline">
              Trader Reset AI
            </span>
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${
                    isActive ? 'text-[#007AFF]' : 'text-[#8892B0] hover:text-white'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                {/* Notification Bell */}
                <div className="relative">
                  <button
                    onClick={handleOpenNotifications}
                    className="relative p-2 text-[#8892B0] hover:text-white transition-colors"
                    data-testid="notification-bell"
                  >
                    <Bell size={22} weight={unreadCount > 0 ? 'fill' : 'regular'} />
                    {unreadCount > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-[#FF3B30] text-white text-[10px] font-bold px-1"
                        data-testid="notification-badge"
                      >
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>

                  {/* Notification Dropdown */}
                  {notifOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
                      <div
                        className="absolute right-0 top-full mt-2 w-80 max-h-96 bg-[#131313] border border-white/10 overflow-hidden z-50 shadow-xl shadow-black/40"
                        data-testid="notification-dropdown"
                      >
                        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                          <span className="text-sm font-bold text-white uppercase tracking-wider">Notifications</span>
                          {notifications.length > 0 && (
                            <span className="text-xs text-[#5C6B82]">{notifications.length} recent</span>
                          )}
                        </div>
                        <div className="overflow-y-auto max-h-[340px]">
                          {notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center text-sm text-[#5C6B82]">
                              No notifications yet
                            </div>
                          ) : (
                            notifications.map((n) => (
                              <div
                                key={n.id}
                                className={`px-4 py-3 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                                  !n.read ? 'bg-[#007AFF]/5' : ''
                                }`}
                                onClick={() => {
                                  setNotifOpen(false);
                                  navigate('/community');
                                }}
                                data-testid={`notification-item-${n.id}`}
                              >
                                <div className="flex items-start gap-3">
                                  <div className="mt-0.5">
                                    <UserAvatar src={n.actor_avatar} name={n.actor_name} size={28} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-[#CCD6F6]">
                                      <span className="font-bold text-white">{n.actor_name}</span>
                                      {n.type === 'like' ? ' liked your post' : ' commented on your post'}
                                    </p>
                                    <p className="text-xs text-[#5C6B82] truncate mt-0.5">
                                      {n.type === 'comment' && n.comment_preview ? `"${n.comment_preview}"` : n.post_content}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                      {n.type === 'like' ? (
                                        <Heart size={12} className="text-[#FF3B30]" weight="fill" />
                                      ) : (
                                        <ChatCircle size={12} className="text-[#007AFF]" weight="fill" />
                                      )}
                                      <span className="text-[10px] text-[#5C6B82]">{timeAgo(n.created_at)}</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-2 text-[#8892B0] hover:text-white transition-colors"
                      data-testid="user-menu-trigger"
                    >
                      <UserAvatar src={user.avatar_url} name={user.name} size={28} />
                      <span className="hidden sm:inline text-sm">{user.name}</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-[#131313] border-white/10 text-[#CCD6F6] min-w-[200px]">
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="cursor-pointer" data-testid="nav-profile">
                        <UserAvatar src={user.avatar_url} name={user.name} size={16} className="mr-2" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/pricing" className="cursor-pointer" data-testid="nav-pricing">
                        <CreditCard className="mr-2 h-4 w-4" />
                        Subscription
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/my-purchases" className="cursor-pointer" data-testid="nav-my-purchases">
                        <Package className="mr-2 h-4 w-4" />
                        My Purchases
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/about" className="cursor-pointer" data-testid="nav-about">
                        <Info className="mr-2 h-4 w-4" />
                        About
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem 
                      onClick={handleLogout} 
                      className="text-[#FF3B30] cursor-pointer"
                      data-testid="nav-logout"
                    >
                      <ArrowRight className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm text-[#8892B0] hover:text-white uppercase tracking-wider transition-colors"
                  data-testid="nav-login"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="px-6 py-2 bg-[#007AFF] text-white text-sm uppercase tracking-wider font-bold hover:bg-[#005bb5] transition-colors"
                  data-testid="nav-register"
                >
                  Get Started
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-[#8892B0] hover:text-white transition-colors ml-2"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/5 py-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                const isActive = location.pathname === link.href;
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-wider font-bold transition-colors ${
                      isActive ? 'text-[#007AFF] bg-[#007AFF]/10' : 'text-[#8892B0] hover:text-white hover:bg-white/5'
                    }`}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
